import { MenuOutlined } from "@ant-design/icons";
import { Checkbox, Dropdown, Menu, Select } from "antd";
import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import Chart from "react-apexcharts";
import { useSelector } from "react-redux";
import { tlogGraphHelperData } from "../../helpers";
import { logDataHelperFunctions } from "../../helpers/logdata-helper";
import { tlogGraphHelper } from "../../helpers/tlog/tlog-graph";
import {
  tlogHelper,
  applyConversionFactor,
} from "../../helpers/tlog/tlog-helper";
import CommonLoader from "../Loaders/loader";

const { Option } = Select;

const Data360LiveIWMGraph = React.memo(
  ({
    tlogData,
    loader,
    reportColumns,
    tableLimitData,
    type,
    conversionFactors,
  }) => {
    const reducerList = useSelector((state) => state);
    const themeState = reducerList?.switchThemeReducer?.switchTheme;
    const selectedProjectNumber = useSelector(
      (state) =>
        state.data360LiveProjectReducer?.currentProject?.project_number || {}
    );
    const selectedProjectName = useSelector(
      (state) =>
        state.data360LiveProjectReducer?.currentProject?.project_name || {}
    );

    const parseTlogData = useCallback((data) => {
      if (!data || !Array.isArray(data)) return [];

      const parsedData = data.map((item) => ({
        zone: item.zone_name || "Head 1",
        time: logDataHelperFunctions.formatTime(new Date(item.time * 1000)),
        travelSpeed:
          applyConversionFactor(
            item.travel_speed,
            conversionFactors?.travel_speed
          ) || "",
        volts:
          applyConversionFactor(item.volts, conversionFactors?.volts) || "",
        current:
          applyConversionFactor(item.current, conversionFactors?.current) || "",
        heat: applyConversionFactor(item.heat, conversionFactors?.heat) || "",
        status: {
          travelSpeed: item.travel_speed_status,
          volts: item.volts_status,
          current: item.current_status,
          heat: item.heat_status,
        },
      }));
      return parsedData;
    }, []);

    const tlogParsedData = useMemo(
      () => parseTlogData(tlogData),
      [tlogData, parseTlogData]
    );

    const separatedDataByZone = useMemo(
      () => ({
        head1: tlogParsedData.filter((item) => item.zone === "Head 1"),
        head2: tlogParsedData.filter((item) => item.zone === "Head 2"),
        head3: tlogParsedData.filter((item) => item.zone === "Head 3"),
        head4: tlogParsedData.filter((item) => item.zone === "Head 4"),
      }),
      [tlogParsedData]
    );

    const activeHeads = useMemo(
      () =>
        Object.keys(separatedDataByZone).filter(
          (key) => separatedDataByZone[key].length > 0
        ),
      [separatedDataByZone]
    );

    const numberOfActiveHeads = useMemo(
      () => activeHeads.length,
      [activeHeads]
    );

    const [selectedOption, setSelectedOption] = useState("Chart 1");

    const [showHead1, setShowHead1] = useState(true);
    const [showHead2, setShowHead2] = useState(true);
    const [showHead3, setShowHead3] = useState(true);
    const [showHead4, setShowHead4] = useState(true);

    const colorOptions = useMemo(() => {
      const options = [
        {
          value: "Travel Speed (cm/min)",
          label: "Travel Speed (cm/min)",
          color: "#e03540",
          key: "travelSpeed",
        },
        {
          value: "Volts (v)",
          key: "volt",
          label: "Volts (v)",
          color: "#FFA500",
        },
        {
          value: "Current (A)",
          key: "current",
          label: "Current (A)",
          color: "#ee82ee",
        },
        {
          value: "Heat (kJ/cm)",
          label: "Heat (kJ/cm)",
          color: "#008000",
          key: "heat",
        },
      ];

      return tlogGraphHelper.mapTlogGraphColumns(options, reportColumns);
    }, [reportColumns]);

    const cleanData = useCallback(
      (dataArray) =>
        dataArray
          .map((value) => {
            if (value === null || value === undefined) return null;
            // If value is already a number, return it
            if (typeof value === "number") return value;
            // If value is a string, try to parse it
            if (typeof value === "string") {
              const sanitizedValue = value.replace(/-color/g, "");
              return parseFloat(sanitizedValue);
            }
            return null;
          })
          .filter((value) => value !== null && !isNaN(value)),
      []
    );

    const time = useMemo(
      () => separatedDataByZone?.head1.map((item) => item.time.slice(3)) || [],
      [separatedDataByZone]
    );

    const returnTime = useCallback(
      (headType) =>
        separatedDataByZone?.[headType].map((item) => item.time.slice(3)) || [],
      [separatedDataByZone]
    );

    const returnTravelSpeed = useCallback(
      (headType) =>
        cleanData(
          separatedDataByZone?.[headType].map((item) => item.travelSpeed) || []
        ),
      [separatedDataByZone, cleanData]
    );

    const returnVolt = useCallback(
      (headType) =>
        cleanData(
          separatedDataByZone?.[headType].map((item) => item.volts) || []
        ),
      [separatedDataByZone, cleanData]
    );

    const returnCurrent = useCallback(
      (headType) =>
        cleanData(
          separatedDataByZone?.[headType].map((item) => item.current) || []
        ),
      [separatedDataByZone, cleanData]
    );

    const returnHeat = useCallback(
      (headType) =>
        cleanData(
          separatedDataByZone?.[headType].map((item) => item.heat) || []
        ),
      [separatedDataByZone, cleanData]
    );

    const empty = [];

    const getMinMaxValues = useCallback(
      (column) => {
        return tlogGraphHelper.getMinMaxValuesForGraph({
          column: column,
          tableLimitData: tableLimitData,
          tlogData: tlogData,
          type: type,
          reportColumns: reportColumns,
        });
      },
      [tableLimitData, tlogData, type, reportColumns]
    );

    const processedDataRef = useRef({
      head1: { categories: [], data: {}, minMax: {} },
      head2: { categories: [], data: {}, minMax: {} },
      head3: { categories: [], data: {}, minMax: {} },
      head4: { categories: [], data: {}, minMax: {} },
    });

    const getGraphData1 = useCallback(
      ({ headNumber, headType, selectedOption }) => {
        let yAxisColor;
        let yAxisTitle;
        let minValue = 0;
        let maxValue = 0;
        let currentData = [];
        let timeCategories = [];

        switch (selectedOption) {
          case "Travel Speed (cm/min)":
            yAxisColor = "#e03540";
            yAxisTitle = `Head ${headNumber} : ${
              colorOptions.find(
                (ite) => ite.key == tlogGraphHelper.columnsMapping.TRAVEL_SPEED
              )?.label
            }`;
            ({ min: minValue, max: maxValue } = getMinMaxValues(
              tlogHelper.mappingColumnTableLimit.travelSpeed
            ));
            currentData = returnTravelSpeed(headType);
            timeCategories = returnTime(headType);
            break;

          case "Volts (v)":
            yAxisColor = "#FFA500";
            yAxisTitle = `Head ${headNumber} : ${
              colorOptions.find(
                (ite) => ite.key == tlogGraphHelper.columnsMapping.VOLT_V
              )?.label
            }`;
            ({ min: minValue, max: maxValue } = getMinMaxValues(
              tlogHelper.mappingColumnTableLimit.volts
            ));
            currentData = returnVolt(headType);
            timeCategories = returnTime(headType);
            break;

          case "Current (A)":
            yAxisColor = "#ee82ee";
            yAxisTitle = `Head ${headNumber} : ${
              colorOptions.find(
                (ite) => ite.key == tlogGraphHelper.columnsMapping.CURRENT_A
              )?.label
            }`;
            ({ min: minValue, max: maxValue } = getMinMaxValues(
              tlogHelper.mappingColumnTableLimit.current
            ));
            currentData = returnCurrent(headType);
            timeCategories = returnTime(headType);
            break;

          case "Heat (kJ/cm)":
            yAxisColor = "#008000";
            yAxisTitle = `Head ${headNumber} : ${
              colorOptions.find(
                (ite) => ite.key == tlogGraphHelper.columnsMapping.HEAT
              )?.label
            }`;
            ({ min: minValue, max: maxValue } = getMinMaxValues(
              tlogHelper.mappingColumnTableLimit.heat
            ));
            currentData = returnHeat(headType);
            timeCategories = returnTime(headType);
            break;

          default:
            yAxisColor = "#0000FF";
            yAxisTitle = "Chart 1";
            currentData = empty;
            timeCategories = time;
            break;
        }

        // Store the processed data in the ref
        processedDataRef.current[headType] = {
          categories: timeCategories,
          data: {
            ...processedDataRef.current[headType].data,
            [selectedOption]: currentData,
          },
          minMax: {
            ...processedDataRef.current[headType].minMax,
            [selectedOption]: { min: minValue, max: maxValue },
          },
        };

        return tlogGraphHelperData.IWMGraph(
          themeState,
          currentData,
          timeCategories,
          yAxisTitle,
          yAxisColor,
          minValue,
          maxValue,
          selectedProjectNumber,
          selectedProjectName
        );
      },
      [
        colorOptions,
        getMinMaxValues,
        returnTime,
        returnTravelSpeed,
        returnVolt,
        returnCurrent,
        returnHeat,
        time,
        themeState,
        selectedProjectNumber,
        selectedProjectName,
      ]
    );

    const [FirstGraph, setFirstGraph] = useState(
      getGraphData1({
        headNumber: 1,
        headType: "head1",
        selectedOption: selectedOption,
      })
    );
    const [SecondGraph, setSecondGraph] = useState(
      getGraphData1({
        headNumber: 2,
        headType: "head2",
        selectedOption: selectedOption,
      })
    );
    const [ThirdGraph, setThirdGraph] = useState(
      getGraphData1({
        headNumber: 3,
        headType: "head3",
        selectedOption: selectedOption,
      })
    );
    const [forthGraph, setforthGraph] = useState(
      getGraphData1({
        headNumber: 4,
        headType: "head4",
        selectedOption: selectedOption,
      })
    );

    useEffect(() => {
      setFirstGraph(
        getGraphData1({
          headNumber: 1,
          headType: "head1",
          selectedOption: selectedOption,
        })
      );
      setSecondGraph(
        getGraphData1({
          headNumber: 2,
          headType: "head2",
          selectedOption: selectedOption,
        })
      );
      setThirdGraph(
        getGraphData1({
          headNumber: 3,
          headType: "head3",
          selectedOption: selectedOption,
        })
      );
      setforthGraph(
        getGraphData1({
          headNumber: 4,
          headType: "head4",
          selectedOption: selectedOption,
        })
      );
    }, [selectedOption, themeState, tlogParsedData, getGraphData1]);

    const isDataEmpty = useMemo(
      () => FirstGraph.series[0].data.length === 0,
      [FirstGraph]
    );
    const isDataEmpty2 = useMemo(
      () => SecondGraph.series[0].data.length === 0,
      [SecondGraph]
    );
    const isDataEmpty3 = useMemo(
      () => ThirdGraph.series[0].data.length === 0,
      [ThirdGraph]
    );
    const isDataEmpty4 = useMemo(
      () => forthGraph.series[0].data.length === 0,
      [forthGraph]
    );

    const downloadChartCSV = useCallback(
      (graphData, selectedOption, headNumber) => {
        const headType = `head${headNumber}`;
        const storedData = processedDataRef.current[headType];

        if (!storedData || !storedData.categories.length) {
          console.error("No data available for download");
          return;
        }

        const categories = storedData.categories;
        const mainData = storedData.data[selectedOption];
        const minData = graphData.series[1]?.data || [];
        const maxData = graphData.series[2]?.data || [];

        // Get the correct label from colorOptions
        const selectedOptionLabel =
          colorOptions.find((option) => option.value === selectedOption)
            ?.label || selectedOption;

        // Create headers
        const headers = ["Time", `Head ${headNumber} - ${selectedOptionLabel}`];
        if (minData.length > 0) headers.push("Min Limit");
        if (maxData.length > 0) headers.push("Max Limit");

        // Create rows by combining categories with data
        const rows = categories.map((category, index) => {
          const row = [category, mainData[index]];
          if (minData.length > 0) row.push(minData[index]);
          if (maxData.length > 0) row.push(maxData[index]);
          return row;
        });

        // Create CSV content
        const csvContent = [
          headers.join(","),
          ...rows.map((row) => row.join(",")),
        ].join("\n");

        // Create and trigger download
        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute(
          "download",
          `${selectedProjectNumber}_${selectedProjectName}_Head${headNumber}_${selectedOptionLabel}.csv`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      },
      [selectedProjectNumber, selectedProjectName, colorOptions]
    );

    const getChartMenu = useCallback(
      (graphData, selectedOption, headNumber) => (
        <Menu>
          <Menu.Item
            onClick={() =>
              downloadChartCSV(graphData, selectedOption, headNumber)
            }
          >
            Download CSV
          </Menu.Item>
        </Menu>
      ),
      [downloadChartCSV]
    );

    return (
      <>
        {loader ? (
          <div className="slogdata-loader">
            <CommonLoader />
          </div>
        ) : (
          <>
            <div className="iwm-header">
              <div className="iwm-selector">
                <Select
                  value={selectedOption}
                  onChange={(value) => setSelectedOption(value)}
                  getPopupContainer={(trigger) => trigger.parentNode}
                  dropdownStyle={{ minWidth: 200 }}
                >
                  {colorOptions.map((option) => (
                    <Option key={option.value} value={option.value}>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <span
                          style={{
                            display: "inline-block",
                            width: "10px",
                            height: "10px",
                            backgroundColor: option.color,
                            marginRight: "8px",
                          }}
                        />
                        <span style={{ textTransform: "math-auto" }}>
                          {option.label}
                        </span>
                      </div>
                    </Option>
                  ))}
                </Select>
              </div>
              {numberOfActiveHeads === 4 && (
                <div className="iwm-checkbox">
                  <Checkbox
                    checked={showHead1}
                    onChange={() => setShowHead1(!showHead1)}
                  >
                    Head 1
                  </Checkbox>
                  <Checkbox
                    checked={showHead2}
                    onChange={() => setShowHead2(!showHead2)}
                  >
                    Head 2
                  </Checkbox>
                  <Checkbox
                    checked={showHead3}
                    onChange={() => setShowHead3(!showHead3)}
                  >
                    Head 3
                  </Checkbox>
                  <Checkbox
                    checked={showHead4}
                    onChange={() => setShowHead4(!showHead4)}
                  >
                    Head 4
                  </Checkbox>
                </div>
              )}
            </div>
            <div className="iwm-graph">
              <div className="single-line-graph">
                {showHead1 && !isDataEmpty && (
                  <div className="graph-container">
                    <div className="menuOutlined">
                      <Dropdown
                        overlay={getChartMenu(FirstGraph, selectedOption, 1)}
                        trigger={["click"]}
                        placement="bottomRight"
                        getPopupContainer={(trigger) => trigger.parentNode}
                      >
                        <MenuOutlined style={{ fontSize: "16px" }} />
                      </Dropdown>
                    </div>
                    <Chart
                      options={FirstGraph.options}
                      series={FirstGraph.series}
                      type="line"
                      height={300}
                      width={numberOfActiveHeads === 1 ? 1260 : 620}
                      className="Graph-background"
                    />
                  </div>
                )}
                {showHead2 && !isDataEmpty2 && (
                  <div className="graph-container">
                    <div className="menuOutlined">
                      <Dropdown
                        overlay={getChartMenu(SecondGraph, selectedOption, 2)}
                        trigger={["click"]}
                        placement="bottomRight"
                        getPopupContainer={(trigger) => trigger.parentNode}
                      >
                        <MenuOutlined style={{ fontSize: "16px" }} />
                      </Dropdown>
                    </div>
                    <Chart
                      options={SecondGraph.options}
                      series={SecondGraph.series}
                      type="line"
                      height={300}
                      width={numberOfActiveHeads === 1 ? 1260 : 620}
                      className="Graph-background"
                    />
                  </div>
                )}
              </div>
              <div className="single-line-graph">
                {showHead3 && !isDataEmpty3 && (
                  <div className="graph-container">
                    <div className="menuOutlined">
                      <Dropdown
                        overlay={getChartMenu(ThirdGraph, selectedOption, 3)}
                        trigger={["click"]}
                        placement="bottomRight"
                        getPopupContainer={(trigger) => trigger.parentNode}
                      >
                        <MenuOutlined style={{ fontSize: "16px" }} />
                      </Dropdown>
                    </div>
                    <Chart
                      options={ThirdGraph.options}
                      series={ThirdGraph.series}
                      type="line"
                      height={300}
                      width={numberOfActiveHeads === 1 ? 1260 : 620}
                      className="Graph-background"
                    />
                  </div>
                )}
                {showHead4 && !isDataEmpty4 && (
                  <div className="graph-container">
                    <div className="menuOutlined">
                      <Dropdown
                        overlay={getChartMenu(forthGraph, selectedOption, 4)}
                        trigger={["click"]}
                        placement="bottomRight"
                        getPopupContainer={(trigger) => trigger.parentNode}
                      >
                        <MenuOutlined style={{ fontSize: "16px" }} />
                      </Dropdown>
                    </div>
                    <Chart
                      options={forthGraph.options}
                      series={forthGraph.series}
                      type="line"
                      height={300}
                      width={numberOfActiveHeads === 1 ? 1260 : 620}
                      className="Graph-background"
                    />
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </>
    );
  }
);

Data360LiveIWMGraph.displayName = "Data360LiveIWMGraph";

export default Data360LiveIWMGraph;
